/**
 * Permission Service
 * Handles ACM (Access Control Management) permission checks
 */

import { MODULES, getModuleByRoute } from '../config/moduleConfig';

/**
 * Get current user from localStorage
 * @returns {object|null} User object or null
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

/**
 * Check if current user is Super Admin
 * @returns {boolean}
 */
export const isSuperAdmin = () => {
  const user = getCurrentUser();
  return user?.isSuperAdmin === true;
};

/**
 * Check if current user is a team member (non-super admin)
 * @returns {boolean}
 */
export const isTeamMember = () => {
  const user = getCurrentUser();
  return user?.isTeamMember === true;
};

// localStorage key for the super-admin branch switcher selection.
const SELECTED_BRANCH_KEY = 'selectedBranchId';

/**
 * Whether the current user is a global admin (can see/manage ALL branches):
 * super admin OR explicitly granted canAccessAllBranches.
 * @returns {boolean}
 */
export const canAccessAllBranches = () => {
  const user = getCurrentUser();
  return user?.isSuperAdmin === true || user?.canAccessAllBranches === true;
};

/**
 * Whether the current admin is locked to a single branch (role 1, not global).
 * @returns {boolean}
 */
export const isBranchScopedAdmin = () => {
  const user = getCurrentUser();
  if (!user || user.role !== 1) return false;
  return !canAccessAllBranches();
};

/**
 * The branch a branch-scoped admin belongs to (primaryBranchId), or null.
 * @returns {string|null}
 */
export const getUserBranchId = () => {
  const user = getCurrentUser();
  return user?.primaryBranchId ? String(user.primaryBranchId) : null;
};


/**
 * Whether the signed-in admin has VIEW-ONLY access — the proposal's
 * "Branch Staff (operational view only)" tier.
 *
 * The server is the real enforcement: isAdmin / hasModuleAccess reject any
 * non-GET from these accounts. This exists only so the UI stops offering
 * buttons that are guaranteed to 403 — never treat it as the security boundary.
 *
 * Only an explicit "view" restricts, so accounts created before this field
 * existed keep full access.
 * @returns {boolean}
 */
export const isViewOnly = () => {
  const user = getCurrentUser();
  return user?.accessLevel === "view";
};

/**
 * Inverse of isViewOnly, for readability at call sites:
 *   {canWrite() && <Button>Add</Button>}
 * @returns {boolean}
 */
export const canWrite = () => !isViewOnly();

/**
 * Readable name of the branch a branch-scoped admin belongs to.
 * Populated on login as a separate field so `primaryBranchId` can stay a plain
 * id string (it is sent as the x-branch-id header). Falls back to the code,
 * then to null when the login predates this field.
 * @returns {string|null}
 */
export const getUserBranchName = () => {
  const user = getCurrentUser();
  const b = user?.primaryBranch;
  if (!b) return null;
  return b.name || b.code || null;
};

/**
 * Branch IDs assigned to the current admin (for global admins this may be empty).
 * @returns {string[]}
 */
export const getAssignedBranches = () => {
  const user = getCurrentUser();
  return (user?.assignedBranches || []).map(String);
};

/**
 * Read the super-admin's persisted branch switcher selection (null = all).
 * @returns {string|null}
 */
export const getSelectedBranchId = () => {
  try {
    const v = localStorage.getItem(SELECTED_BRANCH_KEY);
    return v || null;
  } catch (error) {
    return null;
  }
};

/**
 * Persist (or clear, when falsy) the super-admin's branch switcher selection.
 * @param {string|null} branchId
 */
export const setSelectedBranchId = (branchId) => {
  try {
    if (branchId) localStorage.setItem(SELECTED_BRANCH_KEY, String(branchId));
    else localStorage.removeItem(SELECTED_BRANCH_KEY);
  } catch (error) {
    /* ignore storage errors */
  }
};

/**
 * The branch id to send as the x-branch-id header on admin requests:
 *  - branch-scoped admin: ALWAYS their own primaryBranchId (server enforces too)
 *  - global admin: the switcher selection (null = all branches)
 * @returns {string|null}
 */
export const getActiveBranchId = () => {
  if (isBranchScopedAdmin()) return getUserBranchId();
  return getSelectedBranchId();
};

/**
 * Get user's allowed module permissions
 * @returns {string[]} Array of module IDs
 */
export const getUserModulePermissions = () => {
  const user = getCurrentUser();

  // Super Admin has access to all modules
  if (user?.isSuperAdmin) {
    return Object.keys(MODULES);
  }

  return user?.modulePermissions || [];
};

/**
 * Check if user has access to a specific module
 * @param {string} moduleId - Module ID to check (e.g., 'ROOM', 'FOOD', 'TEAM')
 * @returns {boolean}
 */
export const hasModuleAccess = (moduleId) => {
  const user = getCurrentUser();

  // Not logged in
  if (!user) return false;

  // Not an admin
  if (user.role !== 1) return false;

  // Super Admin has access to all modules
  if (user.isSuperAdmin) return true;

  // Check if module exists
  const module = MODULES[moduleId];
  if (!module) return false;

  // Check if module is always accessible (e.g., DASHBOARD, NOTIFICATION)
  if (module.alwaysAccessible) return true;

  // Check if module is super admin only
  if (module.superAdminOnly) return false;

  // Check user's module permissions
  const permissions = user.modulePermissions || [];
  return permissions.includes(moduleId);
};

/**
 * Check if user has access to any of the specified modules
 * @param {string[]} moduleIds - Array of module IDs
 * @returns {boolean}
 */
export const hasAnyModuleAccess = (moduleIds) => {
  return moduleIds.some((moduleId) => hasModuleAccess(moduleId));
};

/**
 * Check if user can access a specific route
 * @param {string} path - Route path to check
 * @returns {boolean}
 */
export const canAccessRoute = (path) => {
  const user = getCurrentUser();

  // Not logged in
  if (!user) return false;

  // Not an admin
  if (user.role !== 1) return false;

  // Super Admin can access all routes
  if (user.isSuperAdmin) return true;

  // Find which module this route belongs to
  const module = getModuleByRoute(path);

  // Route not associated with any module - allow access
  if (!module) return true;

  // Check if module is always accessible (e.g., DASHBOARD, NOTIFICATION)
  if (module.alwaysAccessible) return true;

  // Check if module is super admin only
  if (module.superAdminOnly) return false;

  // Check module access
  return hasModuleAccess(module.id);
};

/**
 * Get all modules the user has access to
 * @returns {object[]} Array of module objects
 */
export const getAllowedModules = () => {
  const user = getCurrentUser();

  if (!user || user.role !== 1) return [];

  // Super Admin gets all modules
  if (user.isSuperAdmin) {
    return Object.values(MODULES);
  }

  // Filter modules based on permissions
  const permissions = user.modulePermissions || [];
  return Object.values(MODULES).filter((module) => {
    // Exclude super admin only modules
    if (module.superAdminOnly) return false;
    return permissions.includes(module.id);
  });
};

/**
 * Get sidebar menu items based on user permissions
 * @returns {object[]} Array of menu items
 */
export const getPermittedMenuItems = () => {
  return getAllowedModules().map((module) => ({
    id: module.id,
    name: module.name,
    icon: module.icon,
    path: module.path,
  }));
};

/**
 * Get the first allowed route for the user
 * Used for redirecting when user accesses unauthorized pages or as default landing page
 * @returns {string} First allowed route path or '/login' if no access
 */
export const getFirstAllowedRoute = () => {
  const user = getCurrentUser();

  // Not logged in
  if (!user) return '/login';

  // Super Admin - dashboard is first
  if (user.isSuperAdmin) return '/dashboard';

  // Get user's permissions
  const permissions = user.modulePermissions || [];

  // Priority order for landing pages
  const priorityModules = ['DASHBOARD', 'FOOD', 'TABLE', 'ROOM', 'USERS', 'AMENITY', 'ATTRACTION', 'COUPON', 'BANNER', 'ANALYTICS', 'TRANSPORTATION'];

  // Find first allowed module in priority order
  for (const moduleId of priorityModules) {
    if (permissions.includes(moduleId)) {
      const module = MODULES[moduleId];
      if (module && module.path) {
        return module.path;
      }
    }
  }

  // If none of priority modules, get first permission
  if (permissions.length > 0) {
    const firstModule = MODULES[permissions[0]];
    if (firstModule && firstModule.path) {
      return firstModule.path;
    }
  }

  // Fallback - should not happen if user has any permissions
  return '/login';
};

const PermissionService = {
  getCurrentUser,
  isSuperAdmin,
  isTeamMember,
  getUserModulePermissions,
  hasModuleAccess,
  hasAnyModuleAccess,
  canAccessRoute,
  getAllowedModules,
  getPermittedMenuItems,
  canAccessAllBranches,
  isBranchScopedAdmin,
  getUserBranchId,
  getAssignedBranches,
  getSelectedBranchId,
  setSelectedBranchId,
  getActiveBranchId,
};

export default PermissionService;
